import { Component, ViewChildren, QueryList, inject, ChangeDetectionStrategy } from '@angular/core';

import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { GenericModalService, ModalSettingsMessage, ModalSelectElement, SelectMultiChoiceTypes } from '../shared/generic-modal/generic-modal.service';
import { LinkItemPickerComponent } from '../shared/link-item-picker/link-item-picker.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { ConversionTable } from './models/conversion-table';
import { ConversionTableService } from './services/conversion-table.service';
import { CurrentUserService } from '../user/services/current-user.service';
import { PartnerComponentBase } from './partner-search-details.base';
import { PartnerConversionTableComponent } from './partner-conversion-table.component';
import { PartnerConversionTableStructureComponent } from './partner-conversion-table-structure.component';
import { ChangeLogLinkComponent } from '../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../shared/change-log/change-log-link-entity/change-log-link-entity.directive';

@Component({
  templateUrl: './partner-search-details-conversiontables.component.html',
  styleUrl: './partner-search-details-conversiontables.component.scss',
  selector: 'ep-partner-details-conversiontables',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [LinkItemPickerComponent, PermissionRequiredDirective, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, PartnerConversionTableComponent, PartnerConversionTableStructureComponent, ChangeLogLinkComponent, ChangeLogLinkEntityDirective]
})
export class PartnerSearchDetailsConversionTablesComponent extends PartnerComponentBase {
  private conversionTableService = inject(ConversionTableService);
  private modalService = inject(GenericModalService);
  private loadingPanelService = inject(LoadingPanelService);
  private currentUserService = inject(CurrentUserService);

  public conversionTables: ConversionTable[] = [];
  public selectedConversionTable: ConversionTable = null;
  public selectedConversionTableToEdit: ConversionTable = null;
  public partnerId: number;
  public isEditing = false;
  public editAllowed = false;
  public deleteAllowed = false;
  public partnerSpecificAllowed = false;
  public exportAllowed = false;
  public selectedTab = 'values';
  public isCreatingNew = false;
  public hasPermission = false;
  public conversionTableId: number;

  @ViewChildren(PartnerConversionTableComponent) conversionTableComponents: QueryList<PartnerConversionTableComponent>;

  async partnerSet(): Promise<void> {
    this.partnerId = this.selectedPartner?.PartnersId ?? 0; 
    this.selectedConversionTable = null;
    this.conversionTableId = null;

    this.hasPermission = this.currentUserService.hasPermission('Partner_Action_ConversionTablesShow') || this.currentUserService.hasPermission('Partner_Action_ConversionTablesShowUniversal');
    if (this.hasPermission && this.selectedPartner?.PartnersId) {
      const tables = await this.conversionTableService.getConversionTablesLight(this.selectedPartner.PartnersId);
      this.conversionTables = tables.sort((ct1, ct2) => ct1.Name.localeCompare(ct2.Name));
    }
    else {
      this.conversionTables = [];
    }
  }

  selectedConversionTableChanged(conversionTableId?: number) {
    if (conversionTableId) {
      this.conversionTableId = conversionTableId;
      this.selectedTab = 'values';
            this.loadingPanelService.showLoadingPanelDirect(`Loading Conversion Table data`);
            this.conversionTableService.getFullConversionTable(conversionTableId, this.partnerId, false).then(ct => {

        if (ct.IsUniversal) {
          this.editAllowed = this.currentUserService.hasPermission('Partner_Action_ConversionTableStructureEditUniversal');
          this.deleteAllowed = this.currentUserService.hasPermission('Partner_Action_ConversionTableStructureDeleteUniversal');
          this.partnerSpecificAllowed = this.currentUserService.hasPermission('Partner_Action_ConversionTableDataShowPartnerSpecific');
        }
        else {
          this.editAllowed = this.currentUserService.hasPermission('Partner_Action_ConversionTableStructureEdit');
          this.deleteAllowed = this.currentUserService.hasPermission('Partner_Action_ConversionTableStructureDelete');
        }
        this.exportAllowed = this.currentUserService.hasPermission('Partner_Action_ConversionTableStructureExport');

        this.selectedConversionTable = ct;
        this.loadingPanelService.hideLoadingPanel();
      });
    }
    else {
      this.selectedConversionTable = null;
      this.conversionTableId = null;
    }
  }

  changeEditMode(isEditing: boolean) {
    this.isEditing = isEditing;
    super.setEditMode(isEditing);
  }

  new() {
    this.changeEditMode(true);
    this.isCreatingNew = true;
    this.selectedConversionTableToEdit = new ConversionTable();
  }

  edit() {
    this.changeEditMode(true);
    this.isCreatingNew = false;
    this.selectedConversionTableToEdit = this.selectedConversionTable;
  }

  delete() {
    this.modalService.confirmDirect('Delete', `Delete ${this.selectedConversionTable.Name}?`).then(() => {
      this.loadingPanelService.showLoadingPanelDirect('Deleting Conversion Table...');
            this.conversionTableService.deleteConversionTable(this.selectedConversionTable.Id).then(() => {
                this.partnerSet().then(() => {
          this.loadingPanelService.hideLoadingPanel();
        });
      });
    }).catch(() => { });
  }

  structureSaved(table: ConversionTable) {

    if (this.isCreatingNew) {
      this.conversionTables.push(table);
    }
    else {
      // The items in the dropdown does not have the same object-reference as the loaded conversion-table
      // Therefore merge the changes into the item in the dropdown so the name will be shown correct - if it has been changed.
      const existingTable = this.conversionTables.find(ct => ct.Id === table.Id);
      if (existingTable) {
        Object.assign(existingTable, table);
      }

      // Make sure new columns are shown and removed columns are removed...
      this.conversionTableComponents.forEach(ctc => ctc.parseConversionTable());
    }

    this.conversionTables.sort((ct1, ct2) => ct1.Name.localeCompare(ct2.Name));

    this.selectedConversionTableToEdit = null;
    this.changeEditMode(false);
    this.isCreatingNew = false;

    // Reload the conversion table to reflect the new structural changes.
    this.selectedConversionTableChanged(table.Id);
  }

  structureCancelled() {
    this.changeEditMode(false);
    this.isCreatingNew = false;
    this.selectedConversionTableToEdit = null;
  }

  export() {
    this.conversionTableService.exportConversionTable(this.selectedConversionTable.Id);
  }

  importStructure() {
    const dropdownOptions: ModalSelectElement[] = [];
    dropdownOptions.push(new ModalSelectElement('Import table structure and all values', 0));
    dropdownOptions.push(new ModalSelectElement('Import table structure and any global values', 1));
    dropdownOptions.push(new ModalSelectElement('Import only table structure', 2));

    this.modalService.selectMultiChoiceDirect('Import Conversion Table', 'Please select what to import', dropdownOptions, dropdownOptions[0], SelectMultiChoiceTypes.DropDown)
      .then((option: ModalSelectElement) => {
        this.modalService.fileSelectorDirect('Upload Conversion Table File', 'Select the XML file to upload', false, 'Upload').then((files) => {
          if (files && files.length > 0) {
            this.loadingPanelService.showLoadingPanelDirect('Uploading...');

            this.conversionTableService.importConversionTable(this.partnerId, files[0], option.value).then((result) => {
              const modalSettings = new ModalSettingsMessage();
              modalSettings.body = '<pre>' + result.Log + '</pre>';

              if (result.ImportResultType === 'Success') {
                modalSettings.title = 'Upload Successful';

                                this.modalService.message(modalSettings).then(() => {
                                    this.partnerSet().then(() => {
                    this.loadingPanelService.hideLoadingPanel();
                  });
                });
              }
              else if (result.ImportResultType === 'PartiallySuccess') {
                modalSettings.title = 'Upload Partially Succeeded';
                modalSettings.size = 'lg';

                                this.modalService.message(modalSettings).then(() => {
                  this.loadingPanelService.hideLoadingPanel();
                                    this.partnerSet();
                });
              }
              else {
                modalSettings.title = 'An error has occurred during import';
                modalSettings.size = 'lg';
                                this.modalService.message(modalSettings).then(() => {
                  this.loadingPanelService.hideLoadingPanel();
                                    this.partnerSet();
                });
              }

            });

          }
        }).catch(() => { });
      }).catch(() => { });
  }
}
